import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { getAuthHeaders } from "../utils/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * OracleSection - Sophisticated Horoscope Display with Avatar-Focused Design
 * 
 * Features:
 * - Central Oracle Avatar as the visual anchor
 * - Glass-morphism cards for El Eco, El Pulso, El Presagio
 * - Mystical serif typography with gold accents
 * - Revelation-style fade-in animations
 */
export const OracleSection = () => {
    const { t, language } = useLanguage();
    const [view, setView] = useState('pulse');
    const [horoscopes, setHoroscopes] = useState({});
    const [loading, setLoading] = useState(true);

    // Listen for language changes and clear cached horoscopes
    useEffect(() => {
        const handleLanguageChange = () => {
            console.log('[OracleSection] Language changed, clearing cached horoscopes');
            setHoroscopes({});
            setLoading(true);
        };
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    // Fetch horoscope for current view
    useEffect(() => {
        const fetchHoroscope = async () => {
            const config = viewConfig[view];
            const timeframe = config?.timeframe || 'today';
            
            const cachedLang = horoscopes[view]?._lang;
            if (horoscopes[view] && cachedLang === language) {
                setLoading(false);
                return;
            }
            
            // Get user name from onboarding data FIRST - before any API call
            let userName = '';
            try {
                const onboardingData = localStorage.getItem('astral_belonging_onboarding');
                if (onboardingData) {
                    const parsed = JSON.parse(onboardingData);
                    userName = parsed?.userData?.name || '';
                }
            } catch (e) {
                console.warn('Could not parse onboarding data');
            }
            
            // API GUARD: Do NOT fire horoscope requests if userName is missing or < 3 chars
            // This prevents 400 errors from being sent to the server
            if (!userName || userName.trim().length < 3) {
                console.log("⏳ OracleSection: Waiting for valid userName before API call (current:", userName, ")");
                // Use fallback horoscope instead of making bad API call
                setHoroscopes(prev => ({
                    ...prev,
                    [view]: getFallbackHoroscope(view, t, language)
                }));
                setLoading(false);
                return;
            }
            
            setLoading(true);
            
            try {
                // Get auth headers (PHASE 2B: prefers Firebase ID token)
                const headers = await getAuthHeaders();
                
                // Build URL with user_name parameter
                let url = `${API_URL}/api/horoscope/${timeframe}?lang=${language}`;
                url += `&user_name=${encodeURIComponent(userName)}`;
                
                console.log("🔮 OracleSection: Fetching horoscope for", userName);
                
                const response = await fetch(url, {
                    credentials: "include",
                    headers
                });
                
                if (response.ok) {
                    const data = await response.json();
                    // Inject user name if API didn't return it but we have it
                    if (!data.user_name && userName) {
                        data.user_name = userName;
                    }
                    setHoroscopes(prev => ({
                        ...prev,
                        [view]: { ...data, _lang: language }
                    }));
                } else {
                    console.warn("OracleSection: API returned", response.status);
                    setHoroscopes(prev => ({
                        ...prev,
                        [view]: getFallbackHoroscope(view, t, language)
                    }));
                }
            } catch (err) {
                console.error("Horoscope fetch error:", err);
                setHoroscopes(prev => ({
                    ...prev,
                    [view]: getFallbackHoroscope(view, t, language)
                }));
            } finally {
                // UNIVERSAL KILL-SWITCH: Always stop loading, even on 400/422 errors
                setLoading(false);
            }
        };

        fetchHoroscope();
    }, [view, language]);

    const horoscope = horoscopes[view];
    
    // View configurations - 5 timeframes
    const viewConfig = {
        echo: { 
            label: language === 'es' ? 'El Eco' : 'The Echo', 
            subtext: language === 'es' ? 'Los ecos del ayer' : 'Echoes of yesterday',
            icon: "◐",
            gradient: "from-indigo-900/30 to-purple-900/30",
            timeframe: 'yesterday'
        },
        pulse: { 
            label: language === 'es' ? 'El Pulso' : 'The Pulse', 
            subtext: language === 'es' ? 'El latido cósmico de hoy' : 'Today\'s cosmic heartbeat',
            icon: "☉",
            gradient: "from-amber-900/30 to-orange-900/30",
            timeframe: 'today'
        },
        omen: { 
            label: language === 'es' ? 'El Presagio' : 'The Omen', 
            subtext: language === 'es' ? 'Las sombras del mañana' : 'Shadows of tomorrow',
            icon: "◑",
            gradient: "from-purple-900/30 to-pink-900/30",
            timeframe: 'tomorrow'
        },
        cycle: { 
            label: language === 'es' ? 'El Ciclo' : 'The Cycle', 
            subtext: language === 'es' ? 'El ciclo lunar de este mes' : 'This month\'s lunar cycle',
            icon: "☽",
            gradient: "from-slate-800/30 to-blue-900/30",
            timeframe: 'month'
        },
        orbit: { 
            label: language === 'es' ? 'La Órbita' : 'The Orbit', 
            subtext: language === 'es' ? 'Tu vuelta al sol' : 'Your solar return',
            icon: "◯",
            gradient: "from-yellow-900/30 to-amber-900/30",
            timeframe: 'year'
        }
    };
    
    const currentConfig = viewConfig[view];
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <motion.div 
            className="px-4 py-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
        >
            {/* Collapsible Section Header - Clickable */}
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full text-center mb-4 group cursor-pointer"
            >
                <div className="flex items-center justify-center gap-3">
                    <div className="w-8 h-px bg-gradient-to-r from-transparent to-primary/40" />
                    <h2 className="text-xs font-cinzel text-primary/60 uppercase tracking-[0.3em] flex items-center gap-2 group-hover:text-primary/80 transition-colors">
                        <span>✦</span>
                        {t('dashboard.astral_horoscope')}
                        <motion.span
                            animate={{ rotate: isExpanded ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="text-primary/40"
                        >
                            ▾
                        </motion.span>
                    </h2>
                    <div className="w-8 h-px bg-gradient-to-l from-transparent to-primary/40" />
                </div>
                {!isExpanded && (
                    <p className="text-primary/30 text-[10px] font-inter mt-2">
                        {language === 'es' ? 'Toca para revelar tu horóscopo' : 'Tap to reveal your horoscope'}
                    </p>
                )}
            </button>

            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        {/* Timeframe Tabs - 5 Glass Cards */}
                        <div className="flex justify-center gap-2 mb-5 flex-wrap">
                {['echo', 'pulse', 'omen', 'cycle', 'orbit'].map((v) => (
                    <motion.button
                        key={v}
                        onClick={() => setView(v)}
                        className={`relative px-3 py-2 rounded-xl text-[10px] font-cinzel uppercase tracking-wider transition-all ${
                            view === v ? 'text-primary' : 'text-foreground/40 hover:text-foreground/60'
                        }`}
                        style={{
                            background: view === v 
                                ? 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)'
                                : 'rgba(255,255,255,0.02)',
                            border: view === v 
                                ? '1px solid rgba(212,175,55,0.4)' 
                                : '1px solid rgba(255,255,255,0.05)',
                            backdropFilter: 'blur(10px)'
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <span className="flex items-center gap-1.5">
                            <span className="text-sm opacity-70">{viewConfig[v].icon}</span>
                            <span>{viewConfig[v].label}</span>
                        </span>
                        {view === v && (
                            <motion.div
                                className="absolute -bottom-1 left-1/2 w-6 h-0.5 bg-primary rounded-full"
                                layoutId="activeTab"
                                style={{ x: '-50%' }}
                            />
                        )}
                    </motion.button>
                ))}
            </div>

            {/* Current View Label */}
            <motion.div 
                className="text-center mb-4"
                key={view}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <p className="text-[10px] font-inter text-foreground/40 italic">
                    {currentConfig.subtext}
                </p>
            </motion.div>

            {/* Main Content Card - Glass Morphism */}
            <motion.div
                className="rounded-2xl overflow-hidden"
                style={{
                    background: 'linear-gradient(145deg, rgba(20,18,28,0.85) 0%, rgba(12,10,18,0.95) 100%)',
                    border: '1px solid rgba(212, 175, 55, 0.15)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)'
                }}
            >
                <AnimatePresence mode="wait">
                    {loading ? (
                        <motion.div 
                            key="loading"
                            className="flex flex-col items-center justify-center py-16"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            {/* Mystical skeleton loader - pulsing stars */}
                            <div className="relative w-16 h-16">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <motion.div
                                        key={i}
                                        className="absolute w-2 h-2 rounded-full bg-primary/60"
                                        style={{
                                            top: '50%',
                                            left: '50%',
                                            transform: `rotate(${i * 72}deg) translateY(-24px)`
                                        }}
                                        animate={{
                                            opacity: [0.3, 1, 0.3],
                                            scale: [0.8, 1.2, 0.8]
                                        }}
                                        transition={{
                                            duration: 1.5,
                                            repeat: Infinity,
                                            delay: i * 0.2
                                        }}
                                    />
                                ))}
                                <motion.div
                                    className="absolute inset-0 flex items-center justify-center text-2xl"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                                >
                                    ✧
                                </motion.div>
                            </div>
                            <p className="mt-4 text-xs font-inter text-foreground/40 italic">
                                {t('dashboard.channeling')} {currentConfig.label}...
                            </p>
                        </motion.div>
                    ) : horoscope ? (
                        <motion.div
                            key="content"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            {/* Cosmic Message - The Main Reading */}
                            <div className="p-6 text-center border-b border-primary/10">
                                <motion.h3 
                                    className="text-[11px] font-cinzel uppercase tracking-[0.2em] mb-4 text-primary"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    ✦ {t('dashboard.ruthless_insight')} ✦
                                </motion.h3>
                                
                                <motion.p 
                                    className="text-sm font-inter text-foreground/80 leading-relaxed max-w-md mx-auto"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.4, duration: 0.8 }}
                                >
                                    {horoscope.cosmic_message}
                                </motion.p>
                                
                                {/* Show user name or zodiac sign */}
                                {(horoscope.user_name || horoscope.zodiac_sign) && horoscope.zodiac_sign !== "seeker" && (
                                    <motion.div 
                                        className="mt-4 flex items-center justify-center gap-2"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.6 }}
                                    >
                                        <span className="text-[9px] font-inter text-primary/40 uppercase tracking-wider">
                                            {t('dashboard.written_for')}
                                        </span>
                                        <span className="text-xs font-cinzel text-primary/70 capitalize">
                                            {horoscope.user_name || horoscope.zodiac_sign}
                                        </span>
                                        {horoscope.user_name && horoscope.zodiac_sign && (
                                            <span className="text-[9px] font-inter text-primary/30">
                                                ({t(`zodiac.${horoscope.zodiac_sign.toLowerCase()}`) || horoscope.zodiac_sign})
                                            </span>
                                        )}
                                    </motion.div>
                                )}
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-3 divide-x divide-primary/10">
                                {/* Planetary Aspects */}
                                <motion.div 
                                    className="p-4"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.5 }}
                                >
                                    <h4 className="text-[9px] font-cinzel uppercase tracking-wider text-purple-400 mb-2 text-center">
                                        {t('dashboard.planetary_aspects')}
                                    </h4>
                                    <ul className="space-y-1">
                                        {horoscope.planetary_aspects?.slice(0, 2).map((aspect, i) => (
                                            <li key={i} className="text-[10px] font-inter text-foreground/50 text-center">
                                                {aspect}
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>

                                {/* Favored */}
                                <motion.div 
                                    className="p-4"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6 }}
                                >
                                    <h4 className="text-[9px] font-cinzel uppercase tracking-wider text-green-500 mb-2 text-center">
                                        {t('dashboard.favored')}
                                    </h4>
                                    <ul className="space-y-1">
                                        {horoscope.favored?.slice(0, 2).map((item, i) => (
                                            <li key={i} className="text-[10px] font-inter text-foreground/50 text-center">
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>

                                {/* Forbidden */}
                                <motion.div 
                                    className="p-4"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.7 }}
                                >
                                    <h4 className="text-[9px] font-cinzel uppercase tracking-wider text-red-400 mb-2 text-center">
                                        {t('dashboard.forbidden')}
                                    </h4>
                                    <ul className="space-y-1">
                                        {horoscope.forbidden?.slice(0, 2).map((item, i) => (
                                            <li key={i} className="text-[10px] font-inter text-foreground/50 text-center">
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            </div>

                            {/* Lucky Elements & Share */}
                            <motion.div 
                                className="p-4 border-t border-primary/10 flex items-center justify-between"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.8 }}
                            >
                                <div className="flex items-center gap-4">
                                    <span className="text-[9px] font-inter text-primary/50">
                                        {t('dashboard.lucky_color')}: <span className="text-primary/70">{horoscope.lucky_color}</span>
                                    </span>
                                    <span className="text-[9px] font-inter text-primary/50">
                                        {t('dashboard.peak_hour')}: <span className="text-primary/70">{horoscope.peak_hour}</span>
                                    </span>
                                </div>
                                
                                <button
                                    onClick={() => {
                                        const text = `${currentConfig.label}: ${horoscope.cosmic_message} ✨ #AstralBelonging`;
                                        if (navigator.share) {
                                            navigator.share({ title: 'My Cosmic Fate', text }).catch(() => {});
                                        } else {
                                            navigator.clipboard.writeText(text);
                                        }
                                    }}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[9px] font-inter uppercase tracking-wider transition-all hover:scale-105"
                                    style={{ 
                                        background: 'rgba(212, 175, 55, 0.1)',
                                        border: '1px solid rgba(212, 175, 55, 0.3)',
                                        color: '#D4AF37'
                                    }}
                                    data-testid="share-fate-btn"
                                >
                                    <span>✦</span>
                                    <span>{t('dashboard.share_fate')}</span>
                                </button>
                            </motion.div>
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

/**
 * Fallback horoscope data when API is unavailable
 */
function getFallbackHoroscope(view, t, language) {
    const isSpanish = language?.startsWith('es');
    
    const fallbacks = {
        echo: {
            cosmic_message: isSpanish 
                ? "Los ecos de ayer aún susurran verdades que te negaste a escuchar. Hoy no es para lamentos, es para entender qué parte de ti sigue atrapada en lo que fue."
                : "Yesterday's echoes still whisper truths you refused to hear. Today is not for regrets—it's for understanding what part of you remains trapped in what was.",
            planetary_aspects: [
                isSpanish ? "Luna en reflexión" : "Moon in reflection",
                isSpanish ? "Saturno recuerda" : "Saturn remembers"
            ],
            favored: [
                isSpanish ? "Introspección profunda" : "Deep introspection",
                isSpanish ? "Soltar el pasado" : "Releasing the past"
            ],
            forbidden: [
                isSpanish ? "Aferrarse a lo que fue" : "Clinging to what was",
                isSpanish ? "Repetir errores" : "Repeating mistakes"
            ],
            lucky_color: isSpanish ? "Plata" : "Silver",
            peak_hour: "3:00 AM",
            _lang: language
        },
        pulse: {
            cosmic_message: isSpanish
                ? "El universo no te debe nada, pero hoy te ofrece una oportunidad. La energía presente es tuya para moldear. ¿La desperdiciarás en dudas o la convertirás en acción?"
                : "The universe owes you nothing, but today it offers an opportunity. The present energy is yours to shape. Will you waste it on doubt or forge it into action?",
            planetary_aspects: [
                isSpanish ? "Sol en tu casa" : "Sun in your house",
                isSpanish ? "Marte impulsa" : "Mars drives forward"
            ],
            favored: [
                isSpanish ? "Decisiones audaces" : "Bold decisions",
                isSpanish ? "Nuevos comienzos" : "New beginnings"
            ],
            forbidden: [
                isSpanish ? "La indecisión" : "Indecision",
                isSpanish ? "Postergar" : "Procrastination"
            ],
            lucky_color: isSpanish ? "Dorado" : "Gold",
            peak_hour: "12:00 PM",
            _lang: language
        },
        omen: {
            cosmic_message: isSpanish
                ? "Mañana trae semillas de lo que plantaste hoy. El cosmos te advierte: cada pensamiento de esta noche se manifestará. Elige bien lo que alimentas en tu mente."
                : "Tomorrow brings seeds of what you plant today. The cosmos warns: every thought tonight will manifest. Choose wisely what you feed your mind.",
            planetary_aspects: [
                isSpanish ? "Venus susurra promesas" : "Venus whispers promises",
                isSpanish ? "Neptuno sueña" : "Neptune dreams"
            ],
            favored: [
                isSpanish ? "Visualización" : "Visualization",
                isSpanish ? "Manifestación" : "Manifestation"
            ],
            forbidden: [
                isSpanish ? "Pensamientos negativos" : "Negative thoughts",
                isSpanish ? "Ignorar señales" : "Ignoring signs"
            ],
            lucky_color: isSpanish ? "Púrpura" : "Purple",
            peak_hour: "11:11 PM",
            _lang: language
        },
        cycle: {
            cosmic_message: isSpanish
                ? "Este ciclo lunar te invita a fluir con las mareas cósmicas. La Luna Nueva siembra intenciones, la Luna Llena las revela. Tu energía mensual se alinea con los ritmos del universo."
                : "This lunar cycle invites you to flow with cosmic tides. The New Moon plants intentions, the Full Moon reveals them. Your monthly energy aligns with the rhythms of the universe.",
            planetary_aspects: [
                isSpanish ? "Luna transita tu carta" : "Moon transits your chart",
                isSpanish ? "Ciclo de 28 días activo" : "28-day cycle active"
            ],
            favored: [
                isSpanish ? "Planificación a largo plazo" : "Long-term planning",
                isSpanish ? "Rituales lunares" : "Lunar rituals"
            ],
            forbidden: [
                isSpanish ? "Decisiones impulsivas" : "Impulsive decisions",
                isSpanish ? "Ignorar tu intuición" : "Ignoring your intuition"
            ],
            lucky_color: isSpanish ? "Plateado lunar" : "Lunar silver",
            peak_hour: isSpanish ? "Luna Llena" : "Full Moon",
            _lang: language
        },
        orbit: {
            cosmic_message: isSpanish
                ? "Tu órbita anual marca el regreso solar, un renacer cósmico. Este año trae lecciones de crecimiento y transformación. Cada temporada te acerca más a tu verdadero propósito estelar."
                : "Your annual orbit marks the solar return, a cosmic rebirth. This year brings lessons of growth and transformation. Each season draws you closer to your true stellar purpose.",
            planetary_aspects: [
                isSpanish ? "Retorno Solar activo" : "Solar Return active",
                isSpanish ? "Júpiter expande horizontes" : "Jupiter expands horizons"
            ],
            favored: [
                isSpanish ? "Visión a largo plazo" : "Long-term vision",
                isSpanish ? "Metas anuales" : "Yearly goals"
            ],
            forbidden: [
                isSpanish ? "Perder la fe" : "Losing faith",
                isSpanish ? "Conformismo" : "Complacency"
            ],
            lucky_color: isSpanish ? "Oro solar" : "Solar gold",
            peak_hour: isSpanish ? "Tu cumpleaños" : "Your birthday",
            _lang: language
        }
    };
    
    return fallbacks[view] || fallbacks.pulse;
}

export default OracleSection;
